import { useState, useEffect } from "react";

export default function InsertFittings({ customerId = 12 }) {
  const [formData, setFormData] = useState({
    dress_type: "Shirt",
    height_cm: "",
    breadth_cm: "",
    hand_size_cm: "",
    waist_cm: "",
    shoulder_cm: "",
    hip_cm: "",
    neck_cm: "",
    thigh_cm: "",
    knee_cm: "",
    bottom_cm: "",
    inseam_cm: "",
    arm_hole_cm: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [fittings, setFittings] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [fetchError, setFetchError] = useState("");

  // Fetch existing fittings
  const fetchFittings = async () => {
    setFetching(true);
    try {
      const res = await fetch(
        `https://tailor.beezetsoftwaresolution.com/api/costume/get_costume_fittings.php?customer_id=${customerId}`
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        setFittings(data);
        setFetchError("");
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setFetchError("Failed to load fitting data: " + err.message);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchFittings();
  }, [customerId]);

  // Form field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Build visible fields depending on dress type
  const getFieldsByDressType = () => {
    const commonFields = ["height_cm", "notes"];
    switch (formData.dress_type) {
      case "Shirt":
        return [...commonFields, "shoulder_cm", "neck_cm", "breadth_cm", "hand_size_cm", "waist_cm"];
      case "Pant":
        return [...commonFields, "waist_cm", "hip_cm", "thigh_cm", "knee_cm", "bottom_cm", "inseam_cm"];
      case "Kurta":
        return [...commonFields, "shoulder_cm", "neck_cm", "breadth_cm", "waist_cm", "hip_cm", "hand_size_cm", "arm_hole_cm"];
      case "Blouse":
        return [...commonFields, "shoulder_cm", "neck_cm", "breadth_cm", "waist_cm", "hand_size_cm"];
      default:
        return commonFields;
    }
  };

  const visibleFields = getFieldsByDressType();

  const getFieldLabel = (field) =>
    field
      .replace("_cm", "")
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") + (field.includes("_cm") ? " (cm)" : "");

  // Submit to API
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: "", type: "" });

    // Prepare measurements array
    const measurements = Object.keys(formData)
      .filter((key) => key !== "dress_type" && formData[key] !== "")
      .map((key) => ({
        name: getFieldLabel(key).replace(" (cm)", ""),
        value: formData[key] + " cm",
      }));

    if (!customerId || !formData.dress_type || measurements.length === 0) {
      setMessage({ text: "Missing required fields: customer_id, profile_name, or measurements.", type: "error" });
      setLoading(false);
      return;
    }

    const payload = {
      customer_id: customerId,
      profile_name: `${formData.dress_type} Profile`,
      measurements,
    };

    try {
      const res = await fetch(
        "https://tailor.beezetsoftwaresolution.com/api/costume/add_costume_fittings.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (data.success) {
        setMessage({ text: data.message, type: "success" });
        // Reset form
        setFormData({
          dress_type: "Shirt",
          height_cm: "",
          breadth_cm: "",
          hand_size_cm: "",
          waist_cm: "",
          shoulder_cm: "",
          hip_cm: "",
          neck_cm: "",
          thigh_cm: "",
          knee_cm: "",
          bottom_cm: "",
          inseam_cm: "",
          arm_hole_cm: "",
          notes: "",
        });
        // Refresh fittings list
        fetchFittings();
      } else {
        setMessage({ text: data.error || "Failed to save measurements.", type: "error" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Network error. Could not save measurements.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-blue-600 text-white py-4 text-center font-semibold text-lg shadow-md">
        Fitting Details
      </div>

      {/* EXISTING FITTINGS */}
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">
          Existing Fitting Profiles
        </h2>

        {fetching ? (
          <div className="text-center text-gray-600">Loading fittings...</div>
        ) : fetchError ? (
          <div className="text-red-600">{fetchError}</div>
        ) : fittings.length === 0 ? (
          <div className="text-gray-500">No fitting data found.</div>
        ) : (
          <div className="space-y-4">
            {fittings.map((fit, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-blue-700">{fit.profile_name}</h3>
                  <p className="text-sm text-gray-500">
                    Last Updated: {fit.last_updated || "N/A"}
                  </p>
                </div>
                <ul className="list-disc list-inside text-gray-700 space-y-1">
                  {fit.measurements.map((m, i) => (
                    <li key={i}>
                      <span className="font-medium">{m.name || m.measurement_name}: </span>
                      {m.value || m.measurement_value}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* NEW FITTING FORM */}
      <div className="max-w-2xl mx-auto p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">Add New Fitting</h2>
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Dress Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Dress Type *</label>
            <select
              name="dress_type"
              value={formData.dress_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="Shirt">Shirt</option>
              <option value="Pant">Pant</option>
              <option value="Kurta">Kurta</option>
              <option value="Blouse">Blouse</option>
            </select>
          </div>

          {/* Measurement Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {visibleFields.map((field) => {
              if (field === "notes") return null;
              return (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{getFieldLabel(field)}</label>
                  <input
                    type="number"
                    step="0.1"
                    name={field}
                    value={formData[field]}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Enter ${getFieldLabel(field).toLowerCase()}`}
                  />
                </div>
              );
            })}
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter any additional notes"
            ></textarea>
          </div>

          {/* Status Message */}
          {message.text && (
            <div
              className={`mb-4 p-4 rounded-lg ${
                message.type === "success"
                  ? "bg-green-100 text-green-700 border border-green-400"
                  : "bg-red-100 text-red-700 border border-red-400"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
              loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 shadow-lg"
            }`}
          >
            {loading ? "Saving..." : "Add Fitting"}
          </button>
        </div>
      </div>
    </div>
  );
}
